'use client';

import { useEffect, useState } from 'react';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';

/**
 * Public stats for a creator's bio. Shape mirrors legacy
 * `flows/bio/functions/useStoreStatsInfo.ts` `StoreStatsInfo`.
 *
 * The full legacy shape includes shipping/revenue/etc; the
 * bio renderer only consumes `followerCnt` and `profileViews`,
 * so this hook is narrowed accordingly. If a future module
 * needs more fields, extend here.
 */
export interface BioStoreStats {
  followerCnt: number;
  profileViews: number;
}

/**
 * Lazy fetch of the seller metrics for `<sellerId>`. 1:1 port
 * of legacy `useLazyStoreStatsInfo` — single fetch on mount,
 * no subscription. Returns `null` while in flight; never
 * throws (errors are swallowed so a metrics outage doesn't
 * break the bio render).
 *
 * The endpoint is public — anonymous viewers can fetch.
 */
export function useLazyStoreStats(
  sellerId: string | null | undefined,
): BioStoreStats | null {
  const [stats, setStats] = useState<BioStoreStats | null>(null);

  useEffect(() => {
    if (!sellerId) return;

    let cancelled = false;
    const params = new URLSearchParams({ creatorId: sellerId });
    void proxyFetch<BioStoreStats>(`/api/bio/store-stats?${params.toString()}`)
      .then(response => {
        if (cancelled) return;
        setStats(response);
      })
      .catch(() => {
        // Match legacy `onErrorIgnore` — UI shows zeros if the
        // metrics endpoint is unreachable.
      });

    return () => {
      cancelled = true;
    };
  }, [sellerId]);

  return stats;
}
